"use client";

import { ColumnDef, Row } from "@tanstack/react-table";
import { Activity } from "lucide-react";

import { useImagingResultsColumnFilters } from "@/src/components/cards/imaging-results-card/imaging-results-columns";
import { ImagingResultsToolbar } from "@/src/components/cards/imaging-results-card/imaging-results-toolbar";
import { CopyableContent } from "@/src/components/copyable-content";
import { Card, CardContent, CardHeader, CardTitle } from "@/src/components/shadcn/ui/card";
import { DataTable } from "@/src/components/tables/data-table";
import { DataTableColumnHeader } from "@/src/components/tables/data-table-column-header";
import { EnabledFields } from "@/src/lib/schema-types";
import { getClassificationDisplay } from "@/src/lib/utils/classification";
import { formatDate } from "@/src/lib/utils/date-and-phone-utils";
import { isFieldEnabled, hasValue } from "@/src/lib/utils/object-utils";
import { Classification } from "@/src/models/constants/classifications";
import { ImagingResults } from "@/src/models/documents/imaging/imaging-results";

interface ImagingResultsWithClassification extends ImagingResults {
  classification: Classification;
}

interface ImagingResultsTableProps {
  imagingResults: (ImagingResults & { classification: Classification })[];
  enabledFields?: EnabledFields;
}

export function ImagingResultsTable({ imagingResults, enabledFields }: ImagingResultsTableProps) {
  // Get column filters and map the namespaced parameter to the column name
  const [columnFilters] = useImagingResultsColumnFilters();
  const mappedColumnFilters = {
    studyType: columnFilters.studyType,
    imagingType: columnFilters.imagingType,
    bodyPart: columnFilters.bodyPart,
    needsSignature: columnFilters.needsSignature,
    classification: columnFilters.imagingResultsClassification,
  };
  // Data is already in the correct format
  const dataWithClassification: ImagingResultsWithClassification[] = imagingResults;

  const CellWrapper = ({ children }: { children: React.ReactNode }) => {
    return <div className="w-full p-2">{children}</div>;
  };

  const allColumns: ColumnDef<ImagingResultsWithClassification>[] = [
    {
      accessorKey: "classification",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Classification" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("classification") as Classification;
        const displayValue = getClassificationDisplay(value);

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
      filterFn: "arrIncludesSome",
    },
    {
      accessorKey: "studyType",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Study Type" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("studyType") as string;

        return (
          <CellWrapper>
            <span className="max-w-[500px] truncate font-medium no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "imagingType",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Imaging Type" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("imagingType") as string;

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "bodyPart",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Body Part" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("bodyPart") as string;

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "procedure",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Procedure" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("procedure") as string;

        return (
          <CellWrapper>
            <span className="max-w-[500px] truncate font-medium no-select">
              <CopyableContent text={value || "—"} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "orderingProvider",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Provider" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const provider = row.getValue("orderingProvider") as any;
        const displayValue =
          provider && provider.givenName && provider.familyName ? `${provider.givenName} ${provider.familyName}` : "—";
        const npiValue = provider?.npiNumber || null;

        return (
          <CellWrapper>
            <div className="max-w-[500px] truncate font-medium no-select">
              <div>
                <CopyableContent text={displayValue} />
              </div>
              {npiValue && (
                <div className="text-sm text-muted-foreground mt-1">
                  NPI: <CopyableContent text={npiValue} />
                </div>
              )}
            </div>
          </CellWrapper>
        );
      },
      filterFn: "arrIncludesSome",
      accessorFn: (row) => {
        const provider = row.orderingProvider;

        return provider;
      },
    },
    {
      accessorKey: "dateOfEvent",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Event Date" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("dateOfEvent") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateOfImagingResults",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Results Date" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("dateOfImagingResults") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "needsSignature",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Signature" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("needsSignature") as boolean;
        const displayValue = value !== null && value !== undefined ? (value ? "Yes" : "No") : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
      filterFn: (row, id, value) => {
        const rowValue = row.getValue(id) as boolean;

        return value.includes(rowValue ? "true" : "false");
      },
    },
    {
      accessorKey: "needsClarification",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Clarification" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("needsClarification") as boolean;
        const displayValue = value !== null && value !== undefined ? (value ? "Yes" : "No") : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateReceived",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Received Date" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("dateReceived") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateOfDocument",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Document Date" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("dateOfDocument") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
    {
      accessorKey: "dateSigned",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Signed Date" />,
      cell: ({ row }: { row: Row<ImagingResultsWithClassification> }) => {
        const value = row.getValue("dateSigned") as string;
        const displayValue = value ? formatDate(value) || "—" : "—";

        return (
          <CellWrapper>
            <span className="no-select">
              <CopyableContent text={displayValue} />
            </span>
          </CellWrapper>
        );
      },
    },
  ];

  // Filter columns to only show enabled fields that have data
  const columns = allColumns.filter((column) => {
    const fieldName = "accessorKey" in column && typeof column.accessorKey === "string" ? column.accessorKey : "";

    // Always show classification column, check others against enabled fields
    if (fieldName === "classification") return true;

    return (
      isFieldEnabled(enabledFields, fieldName) &&
      dataWithClassification.some((item) => hasValue(item[fieldName as keyof ImagingResultsWithClassification]))
    );
  });

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-md font-semibold flex items-center gap-2">
          <Activity className="h-6 w-6" />
          Imaging Results
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg overflow-hidden border">
          <DataTable
            columns={columns}
            data={dataWithClassification}
            columnFilters={mappedColumnFilters}
            ToolbarComponent={ImagingResultsToolbar}
            getRowUrl={() => ""}
          />
        </div>
      </CardContent>
    </Card>
  );
}
